/**
 * Loan v3.5 — Phase A Admin CMS
 * Combines Banks, Bankers, Outsource Partners, and Loan Dashboard into
 * a single page with tabs to avoid sidebar bloat.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../../../lib/api';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../../components/ui/dialog';
import { Building2, Users, Briefcase, ListChecks, Plus, Trash2, Pencil, Upload, Search, BarChart3, TrendingUp, TrendingDown, Send, Mail, Copy, GitMerge, Sliders, ShieldCheck, BellRing, Clock, AlertTriangle, ArrowRight, RefreshCw, Loader2, FileText, CheckCircle2, XCircle, Download } from 'lucide-react';
import AssessmentWizard from './AssessmentWizard';
import BankRulesTab from './BankRulesTab';

const STATUS_BADGE = {
  draft: 'bg-gray-100 text-gray-700',
  profiling: 'bg-blue-100 text-blue-700',
  standard: 'bg-emerald-100 text-emerald-700',
  special_handling: 'bg-amber-100 text-amber-700',
  in_review: 'bg-violet-100 text-violet-700',
  approved: 'bg-green-100 text-green-700',
  drawdown: 'bg-indigo-100 text-indigo-700',
  closed: 'bg-zinc-100 text-zinc-700',
  rejected: 'bg-red-100 text-red-700',
};

// ============== BANK PERFORMANCE SCORECARD ==============
function ScorecardTab() {
  const [data, setData] = useState({ rows: [], months: 3 });
  const [months, setMonths] = useState(3);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get(`/admin/loans/bank-scorecard?months=${months}`); setData(r.data); }
    catch (e) { toast.error('Load failed'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [months]);

  const totals = data.rows.reduce((acc, r) => ({
    offers: acc.offers + (r.offers_count || 0),
    approvals: acc.approvals + (r.approval_count || 0),
    drawdowns: acc.drawdowns + (r.drawdown_count || 0),
  }), { offers: 0, approvals: 0, drawdowns: 0 });

  // Identify the bank with highest approval rate (>= 5 offers as floor) and lowest
  const ranked = [...data.rows].filter(r => r.offers_count >= 5);
  const best = ranked.length ? [...ranked].sort((a, b) => b.approval_rate_percent - a.approval_rate_percent)[0] : null;
  const worst = ranked.length ? [...ranked].sort((a, b) => a.approval_rate_percent - b.approval_rate_percent)[0] : null;

  return (
    <div className="space-y-3 mt-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-semibold text-gray-800">Bank Performance Scorecard</h2>
        <Select value={String(months)} onValueChange={v => setMonths(parseInt(v))}>
          <SelectTrigger className="w-40" data-testid="scorecard-window-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="1">Last 1 month</SelectItem>
            <SelectItem value="3">Last 3 months</SelectItem>
            <SelectItem value="6">Last 6 months</SelectItem>
            <SelectItem value="12">Last 12 months</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <p className="text-xs text-gray-500">Useful for negotiating better terms with under-performing banks. Calculated from <code>loan_applications.offers[]</code> across <strong>{months} month(s)</strong>.</p>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        <div className="rounded-lg p-3 bg-gray-100"><p className="text-xs text-gray-600 uppercase">Total Offers</p><p className="text-2xl font-bold">{totals.offers}</p></div>
        <div className="rounded-lg p-3 bg-emerald-100"><p className="text-xs text-emerald-700 uppercase">Approved</p><p className="text-2xl font-bold text-emerald-800">{totals.approvals}</p></div>
        <div className="rounded-lg p-3 bg-indigo-100"><p className="text-xs text-indigo-700 uppercase">Drawdown</p><p className="text-2xl font-bold text-indigo-800">{totals.drawdowns}</p></div>
      </div>

      {(best || worst) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {best && <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 flex items-center gap-3" data-testid="scorecard-best-bank">
            <TrendingUp className="w-5 h-5 text-emerald-600" />
            <div><p className="text-xs text-emerald-700">Top performer</p><p className="font-semibold">{best.bank_name} — {best.approval_rate_percent}% approval</p></div>
          </div>}
          {worst && worst.bank_code !== best?.bank_code && <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 flex items-center gap-3" data-testid="scorecard-worst-bank">
            <TrendingDown className="w-5 h-5 text-amber-600" />
            <div><p className="text-xs text-amber-700">Needs attention</p><p className="font-semibold">{worst.bank_name} — {worst.approval_rate_percent}% approval</p></div>
          </div>}
        </div>
      )}

      <div className="border rounded-lg overflow-hidden bg-white">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="text-left p-2">Bank</th>
              <th className="text-right p-2">Offers</th>
              <th className="text-right p-2">Approvals</th>
              <th className="text-right p-2">Approval Rate</th>
              <th className="text-right p-2">Drawdowns</th>
              <th className="text-right p-2">Avg Rate %</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map(r => (
              <tr key={r.bank_code} className="border-t" data-testid={`scorecard-row-${r.bank_code}`}>
                <td className="p-2 font-medium">{r.bank_name} <span className="text-xs text-gray-400 font-mono">{r.bank_code}</span></td>
                <td className="p-2 text-right">{r.offers_count}</td>
                <td className="p-2 text-right">{r.approval_count}</td>
                <td className="p-2 text-right">
                  <Badge className={
                    r.approval_rate_percent >= 60 ? 'bg-emerald-100 text-emerald-700' :
                    r.approval_rate_percent >= 40 ? 'bg-amber-100 text-amber-700' :
                    'bg-red-100 text-red-700'
                  }>{r.approval_rate_percent}%</Badge>
                </td>
                <td className="p-2 text-right">{r.drawdown_count}</td>
                <td className="p-2 text-right">{r.avg_rate_percent || '—'}</td>
              </tr>
            ))}
            {data.rows.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-gray-400">
              {loading ? 'Loading…' : 'No offers in the selected window. The scorecard will populate once bankers start submitting offers (Phase B-2).'}
            </td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function AdminLoanCMS() {
  // V4.24.50 — Honour `?tab=outsource|banks|bankers|dashboard` from URL so
  // sidebar shortcuts (e.g. `/admin/loans?tab=outsource`) land the admin
  // directly on the intended tab.
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'banks';
  const [tab, setTab] = useState(initialTab);
  return (
    <div className="p-6 space-y-4" data-testid="admin-loan-cms-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Loan v3.5 Management</h1>
        <p className="text-sm text-gray-500">Manage participating banks, bankers, outsource partners, and the loan pipeline.</p>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid w-full max-w-6xl grid-cols-9">
          <TabsTrigger value="banks" data-testid="loan-tab-banks"><Building2 className="w-4 h-4 mr-1" /> Banks</TabsTrigger>
          <TabsTrigger value="bankers" data-testid="loan-tab-bankers"><Users className="w-4 h-4 mr-1" /> Bankers</TabsTrigger>
          <TabsTrigger value="outsource" data-testid="loan-tab-outsource"><Briefcase className="w-4 h-4 mr-1" /> Outsource</TabsTrigger>
          <TabsTrigger value="dashboard" data-testid="loan-tab-dashboard"><ListChecks className="w-4 h-4 mr-1" /> Loan Cases</TabsTrigger>
          <TabsTrigger value="profiled" data-testid="loan-tab-profiled"><ShieldCheck className="w-4 h-4 mr-1" /> Profiled</TabsTrigger>
          <TabsTrigger value="reminders" data-testid="loan-tab-reminders"><BellRing className="w-4 h-4 mr-1" /> Reminders</TabsTrigger>
          <TabsTrigger value="rules" data-testid="loan-tab-rules"><Sliders className="w-4 h-4 mr-1" /> Bank Rules</TabsTrigger>
          <TabsTrigger value="funnel" data-testid="loan-tab-funnel"><GitMerge className="w-4 h-4 mr-1" /> Pipeline</TabsTrigger>
          <TabsTrigger value="scorecard" data-testid="loan-tab-scorecard"><BarChart3 className="w-4 h-4 mr-1" /> Scorecard</TabsTrigger>
        </TabsList>
        <TabsContent value="banks"><BanksTab /></TabsContent>
        <TabsContent value="bankers"><BankersTab /></TabsContent>
        <TabsContent value="outsource"><OutsourceTab /></TabsContent>
        <TabsContent value="dashboard"><DashboardTab /></TabsContent>
        <TabsContent value="profiled"><ProfiledTab /></TabsContent>
        <TabsContent value="reminders"><RemindersTab /></TabsContent>
        <TabsContent value="rules"><BankRulesTab /></TabsContent>
        <TabsContent value="funnel"><FunnelTab /></TabsContent>
        <TabsContent value="scorecard"><ScorecardTab /></TabsContent>
      </Tabs>
    </div>
  );
}

// ============== PROFILED LOAN CASES — Phase C Day 4 ==============
function ProfiledTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [shortlistCase, setShortlistCase] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/loan-cases/profiled', { params: { limit: 100 } });
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load profiled cases');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const BAND_BADGE = {
    green: 'bg-emerald-100 text-emerald-700',
    amber: 'bg-amber-100 text-amber-700',
    red: 'bg-red-100 text-red-700',
  };

  return (
    <div className="space-y-4 mt-4" data-testid="loan-profiled-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-semibold text-gray-800 flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-emerald-600" /> Profiled Loan Cases
          </h2>
          <p className="text-xs text-gray-500 mt-1">
            Customers who finished the loan questionnaire. Aicon verdict ready — pick banks from <code>top_banks</code> and fan-out to the participating bankers.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>{loading ? 'Refreshing…' : 'Refresh'}</Button>
      </div>

      <div className="bg-white border rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase text-gray-600">
            <tr>
              <th className="px-3 py-2">Buyer</th>
              <th className="px-3 py-2">Booking</th>
              <th className="px-3 py-2">Band</th>
              <th className="px-3 py-2">Qualified Income</th>
              <th className="px-3 py-2">Recommended Loan</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">AICon</th>
              <th className="px-3 py-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {items.map(c => {
              const band = c.verdict?.risk_band;
              return (
                <tr key={c.id} data-testid={`profiled-row-${c.id}`}>
                  <td className="px-3 py-2">
                    <div className="font-medium text-gray-800">{c.buyer_name || '—'}</div>
                    <div className="text-xs text-gray-500">{c.buyer_email || '—'}</div>
                  </td>
                  <td className="px-3 py-2 text-xs font-mono text-gray-600">{c.booking_id}</td>
                  <td className="px-3 py-2"><Badge className={BAND_BADGE[band] || 'bg-gray-100'}>{band?.toUpperCase() || '—'}</Badge></td>
                  <td className="px-3 py-2">{c.verdict?.qualified_income ? `RM ${Number(c.verdict.qualified_income).toLocaleString()}` : '—'}</td>
                  <td className="px-3 py-2">{c.verdict?.recommended_loan_amount ? `RM ${Number(c.verdict.recommended_loan_amount).toLocaleString()}` : '—'}</td>
                  <td className="px-3 py-2"><Badge variant="outline">{c.status}</Badge></td>
                  <td className="px-3 py-2"><AiconStatusCell caseRow={c} onDone={load} /></td>
                  <td className="px-3 py-2 text-right">
                    <Button
                      size="sm"
                      onClick={() => setShortlistCase(c)}
                      disabled={c.status === 'fanned_out' || !c.verdict?.top_banks?.length}
                      data-testid={`shortlist-btn-${c.id}`}
                    >
                      <Send className="w-3 h-3 mr-1" />
                      {c.status === 'fanned_out' ? 'Fanned Out' : 'Shortlist & Fan-Out'}
                    </Button>
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && (
              <tr><td colSpan={8} className="p-8 text-center text-gray-400">
                {loading ? 'Loading…' : 'No profiled cases yet. They appear here once customers complete the loan questionnaire.'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {shortlistCase && (
        <ShortlistDialog
          caseRow={shortlistCase}
          onClose={() => setShortlistCase(null)}
          onDone={() => { setShortlistCase(null); load(); }}
        />
      )}
    </div>
  );
}

// V4.24.4 — AICon Sync status badge + one-click retry for a loan case
function AiconStatusCell({ caseRow, onDone }) {
  const [busy, setBusy] = useState(false);
  const status = caseRow.aicon_sync_status || null;
  const errMsg = caseRow.aicon_last_sync_error;

  const style = {
    synced: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    documents_upload_failed: 'bg-amber-100 text-amber-800 border-amber-200',
    failed: 'bg-red-100 text-red-800 border-red-200',
    not_configured: 'bg-gray-100 text-gray-600 border-gray-200',
    synced_no_case_id: 'bg-blue-100 text-blue-800 border-blue-200',
  }[status] || 'bg-gray-100 text-gray-500 border-gray-200';

  const label = {
    synced: 'Synced',
    documents_upload_failed: 'Docs failed',
    failed: 'Failed',
    not_configured: 'Off',
    synced_no_case_id: 'Synced (no id)',
  }[status] || (status ? status : 'Not synced');

  const doRetry = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/aicon-sync/retry/${caseRow.id}`);
      const outcome = r.data?.outcome || {};
      if (r.data?.success) toast.success(`AICon retry OK — ${outcome.sync_status}`);
      else toast.error(`AICon retry — ${outcome.sync_status}. ${outcome.error_message || ''}`);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Retry failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="flex items-center gap-2">
      <span
        className={`px-2 py-0.5 rounded text-xs border ${style}`}
        title={errMsg || (caseRow.aicon_case_ref ? `AICon: ${caseRow.aicon_case_ref}` : '')}
        data-testid={`aicon-status-${caseRow.id}`}
      >
        {label}
      </span>
      <Button
        size="sm"
        variant="outline"
        className="h-6 px-2"
        onClick={doRetry}
        disabled={busy}
        data-testid={`aicon-retry-${caseRow.id}`}
      >
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
      </Button>
    </div>
  );
}

function ShortlistDialog({ caseRow, onClose, onDone }) {
  const [selected, setSelected] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [notes, setNotes] = useState('');

  const topBanks = caseRow?.verdict?.top_banks || [];
  const selectedCodes = Object.keys(selected).filter(k => selected[k]);

  const submit = async () => {
    if (selectedCodes.length === 0) { toast.error('Pick at least one bank'); return; }
    setSubmitting(true);
    try {
      const r = await api.post(`/admin/loan-cases/${caseRow.id}/shortlist-fanout`, {
        bank_codes: selectedCodes,
        notes: notes || null,
      });
      const routedOK = (r.data.routing || []).filter(x => x.banker_id).length;
      toast.success(`Fanned out to ${routedOK} banker(s) across ${r.data.selected_banks.length} bank(s)`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Fan-out failed');
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="shortlist-dialog">
        <DialogHeader>
          <DialogTitle>Shortlist Banks — {caseRow.buyer_name || caseRow.id.slice(0, 8)}</DialogTitle>
          <DialogDescription>
            Aicon verdict band: <Badge className="ml-1">{caseRow.verdict?.risk_band?.toUpperCase()}</Badge>
            {' '}· Recommended: RM {Number(caseRow.verdict?.recommended_loan_amount || 0).toLocaleString()}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 max-h-[55vh] overflow-y-auto">
          {topBanks.map((b, i) => {
            const code = b.code || b.bank?.split(' ')[0]?.toUpperCase();
            return (
              <label key={i} className={`flex gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                selected[code] ? 'bg-emerald-50 border-emerald-300' : 'bg-white hover:bg-gray-50'
              }`} data-testid={`shortlist-bank-${code}`}>
                <input type="checkbox" checked={!!selected[code]}
                       onChange={e => setSelected(s => ({ ...s, [code]: e.target.checked }))}
                       className="mt-1" />
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <p className="font-semibold text-gray-800">{b.bank} <span className="text-xs text-gray-500">({code})</span></p>
                    <span className="text-sm font-bold text-emerald-700">RM {Number(b.max_loan).toLocaleString()}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{(b.reasons || []).join(' · ')}</p>
                  <p className="text-xs text-gray-500">~{b.estimated_rate}% · {Math.round((b.probability || 0) * 100)}% match</p>
                </div>
              </label>
            );
          })}
          {topBanks.length === 0 && (
            <p className="text-sm text-gray-500 p-4 text-center">No top banks in this verdict (likely a red-band case — engage outsource broker).</p>
          )}
        </div>

        <div>
          <Label className="text-xs text-gray-600">Notes (optional)</Label>
          <Input value={notes} onChange={e => setNotes(e.target.value)} className="mt-1" placeholder="e.g. Customer prefers Islamic loan" />
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={submitting || selectedCodes.length === 0} data-testid="confirm-shortlist">
            {submitting ? 'Submitting…' : <>Fan Out to {selectedCodes.length} Bank(s) <ArrowRight className="w-3 h-3 ml-1" /></>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============== STALE LOAN CASES — REMINDERS (Phase C Day 3) ==============
function RemindersTab() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [runningCron, setRunningCron] = useState(false);
  const [filters, setFilters] = useState({ min_age_hours: 48, status: '' });
  const [historyCase, setHistoryCase] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = { min_age_hours: filters.min_age_hours, limit: 200 };
      if (filters.status) params.status = filters.status;
      const [r1, r2] = await Promise.all([
        api.get('/admin/loan-cases/stale', { params }),
        api.get('/admin/loan-cases/stale-stats', { params: { min_age_hours: filters.min_age_hours } }),
      ]);
      setItems(r1.data.items || []);
      setStats(r2.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load stale loan cases');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filters.min_age_hours, filters.status]);

  const sendOne = async (caseId) => {
    setSendingId(caseId);
    try {
      const r = await api.post(`/admin/loan-cases/${caseId}/send-reminder`);
      toast.success(`Reminder #${r.data.reminder_number} sent`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reminder failed');
    } finally {
      setSendingId(null);
    }
  };

  const runCron = async (dryRun = false) => {
    setRunningCron(true);
    try {
      const r = await api.post('/admin/loan-cases/run-stale-reminders', null, {
        params: { dry_run: dryRun },
      });
      if (dryRun) {
        toast.info(`Dry run: ${r.data.scanned} cases would be reminded`);
      } else {
        toast.success(`Sent ${r.data.sent} reminders (scanned ${r.data.scanned})`);
        await load();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Run failed');
    } finally {
      setRunningCron(false);
    }
  };

  const STATUS_LABEL = {
    pending_questionnaire: 'Pending Questionnaire',
    link_sent: 'Link Sent',
    filling: 'Mid-Filling',
  };

  return (
    <div className="space-y-4 mt-4" data-testid="loan-reminders-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-semibold text-gray-800 flex items-center gap-2">
            <BellRing className="w-5 h-5 text-amber-600" /> Stale Loan Cases — Reminders
          </h2>
          <p className="text-xs text-gray-500 mt-1">
            Customers stuck in early loan-profiling stages. Auto-cron runs hourly and re-dispatches the magic link once
            the cooldown (48h) has elapsed. Maximum 3 auto-reminders per case.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => runCron(true)} disabled={runningCron} data-testid="reminders-dry-run">
            <Clock className="w-4 h-4 mr-1" /> Dry Run
          </Button>
          <Button size="sm" onClick={() => runCron(false)} disabled={runningCron} data-testid="reminders-run-now">
            <Send className="w-4 h-4 mr-1" /> {runningCron ? 'Running…' : 'Run Reminders Now'}
          </Button>
        </div>
      </div>

      {/* Stats cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="reminders-stats">
          <div className="rounded-lg border bg-amber-50 p-3">
            <p className="text-xs text-amber-700 uppercase">Total Stale</p>
            <p className="text-2xl font-bold text-amber-900">{stats.totals.total}</p>
            <p className="text-xs text-amber-700 mt-1">≥ {stats.min_age_hours}h old</p>
          </div>
          <div className="rounded-lg border bg-slate-50 p-3">
            <p className="text-xs text-slate-600 uppercase">Never Reminded</p>
            <p className="text-2xl font-bold text-slate-800">{stats.totals.no_reminders}</p>
          </div>
          <div className="rounded-lg border bg-blue-50 p-3">
            <p className="text-xs text-blue-700 uppercase">With Reminders</p>
            <p className="text-2xl font-bold text-blue-900">{stats.totals.with_reminders}</p>
          </div>
          <div className="rounded-lg border bg-red-50 p-3">
            <p className="text-xs text-red-700 uppercase flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Maxed Out</p>
            <p className="text-2xl font-bold text-red-900">{stats.totals.max_reminders_hit}</p>
            <p className="text-xs text-red-700 mt-1">Hit {stats.max_reminders}-reminder cap</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Label className="text-xs text-gray-600">Minimum age</Label>
          <Select value={String(filters.min_age_hours)} onValueChange={(v) => setFilters(f => ({ ...f, min_age_hours: parseInt(v) }))}>
            <SelectTrigger className="w-32" data-testid="reminders-age-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="24">≥ 24h</SelectItem>
              <SelectItem value="48">≥ 48h</SelectItem>
              <SelectItem value="72">≥ 72h</SelectItem>
              <SelectItem value="168">≥ 7d</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-gray-600">Status</Label>
          <Select value={filters.status || 'all'} onValueChange={(v) => setFilters(f => ({ ...f, status: v === 'all' ? '' : v }))}>
            <SelectTrigger className="w-56" data-testid="reminders-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All stale statuses</SelectItem>
              <SelectItem value="pending_questionnaire">Pending Questionnaire</SelectItem>
              <SelectItem value="link_sent">Link Sent</SelectItem>
              <SelectItem value="filling">Mid-Filling</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="reminders-refresh">
          {loading ? 'Refreshing…' : 'Refresh'}
        </Button>
      </div>

      {/* Table */}
      <div className="bg-white border rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase text-gray-600">
            <tr>
              <th className="px-3 py-2">Buyer</th>
              <th className="px-3 py-2">Booking</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Age</th>
              <th className="px-3 py-2">Reminders</th>
              <th className="px-3 py-2">Last Reminder</th>
              <th className="px-3 py-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {items.map((c) => (
              <tr key={c.id} data-testid={`stale-case-row-${c.id}`}>
                <td className="px-3 py-2">
                  <div className="font-medium text-gray-800">{c.buyer_name || '—'}</div>
                  <div className="text-xs text-gray-500">{c.buyer_email || c.buyer_phone || '—'}</div>
                </td>
                <td className="px-3 py-2 text-xs font-mono text-gray-600">{c.booking_id}</td>
                <td className="px-3 py-2">
                  <Badge variant="outline">{STATUS_LABEL[c.status] || c.status}</Badge>
                </td>
                <td className="px-3 py-2">
                  <span className={`text-sm ${c.age_hours >= 168 ? 'text-red-700 font-semibold' : c.age_hours >= 72 ? 'text-amber-700' : 'text-gray-700'}`}>
                    {c.age_hours != null ? `${c.age_hours}h` : '—'}
                  </span>
                </td>
                <td className="px-3 py-2">
                  <Badge className={c.reminders_sent >= 3 ? 'bg-red-100 text-red-700' : c.reminders_sent > 0 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}>
                    {c.reminders_sent}/3
                  </Badge>
                </td>
                <td className="px-3 py-2 text-xs text-gray-600">
                  {c.last_reminder_at ? new Date(c.last_reminder_at).toLocaleString() : '—'}
                </td>
                <td className="px-3 py-2 text-right">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => sendOne(c.id)}
                    disabled={sendingId === c.id || c.reminders_sent >= 3}
                    data-testid={`send-reminder-${c.id}`}
                  >
                    <Mail className="w-3 h-3 mr-1" />
                    {sendingId === c.id ? 'Sending…' : c.reminders_sent >= 3 ? 'Max hit' : 'Send Reminder'}
                  </Button>
                  {c.reminders_sent > 0 && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="ml-1"
                      onClick={() => setHistoryCase(c)}
                      data-testid={`view-history-${c.id}`}
                    >
                      History
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-gray-400">
                {loading ? 'Loading…' : 'No stale loan cases — everyone is on track 🎉'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {historyCase && (
        <ReminderHistoryDrawer caseRow={historyCase} onClose={() => setHistoryCase(null)} />
      )}
    </div>
  );
}

// ============== Reminder History Drawer ==============
function ReminderHistoryDrawer({ caseRow, onClose }) {
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/public/v1/loan-cases/${caseRow.id}`);
        setDetail(r.data?.data || {});
      } catch (e) {
        toast.error('Could not load history');
        setDetail(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [caseRow.id]);

  const entries = [
    ...((detail?.link_send_history || []).map(e => ({ ...e, kind: 'Initial dispatch' }))),
    ...((detail?.reminder_history || []).map(e => ({ ...e, kind: `Reminder #${e.reminder_number || '—'}` }))),
  ].sort((a, b) => (a.sent_at || '').localeCompare(b.sent_at || ''));

  return (
    <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="reminder-history-drawer">
        <DialogHeader>
          <DialogTitle>Reminder History — {caseRow.buyer_name || caseRow.id.slice(0, 8)}</DialogTitle>
          <DialogDescription>
            Every magic-link dispatch for booking <code>{caseRow.booking_id}</code>. Newest first.
          </DialogDescription>
        </DialogHeader>
        {loading && <p className="text-sm text-gray-500 p-4">Loading…</p>}
        {!loading && entries.length === 0 && (
          <p className="text-sm text-gray-500 p-4 text-center">No history yet.</p>
        )}
        {!loading && entries.length > 0 && (
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {entries.reverse().map((e, i) => (
              <div key={i} className="border rounded-lg p-3 bg-gray-50">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-sm text-gray-800">{e.kind}</span>
                  <Badge variant="outline" className="text-xs">{e.channel}</Badge>
                </div>
                <p className="text-xs text-gray-600 mt-1">
                  {e.sent_at ? new Date(e.sent_at).toLocaleString() : '—'} · by {e.sent_by_name || e.sent_by || 'system'}
                </p>
                {e.token_expires_at && (
                  <p className="text-xs text-gray-500">
                    Token expires: {new Date(e.token_expires_at).toLocaleString()}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ============== LOAN PIPELINE FUNNEL ==============
function FunnelTab() {
  const [data, setData] = useState(null);
  const [months, setMonths] = useState(6);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try { const r = await api.get(`/admin/loans/funnel?months=${months}`); setData(r.data); }
      catch { toast.error('Load failed'); }
      finally { setLoading(false); }
    })();
  }, [months]);

  const maxCount = data?.stages?.[0]?.count || 1;
  // Color ramp per stage
  const STAGE_COLOR = ['bg-slate-500', 'bg-blue-500', 'bg-emerald-500', 'bg-violet-500', 'bg-green-600', 'bg-indigo-600', 'bg-zinc-600'];

  return (
    <div className="space-y-4 mt-4" data-testid="loan-funnel-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-semibold text-gray-800">Loan Pipeline Funnel</h2>
        <Select value={String(months)} onValueChange={v => setMonths(parseInt(v))}>
          <SelectTrigger className="w-40" data-testid="funnel-window-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="1">Last 1 month</SelectItem>
            <SelectItem value="3">Last 3 months</SelectItem>
            <SelectItem value="6">Last 6 months</SelectItem>
            <SelectItem value="12">Last 12 months</SelectItem>
            <SelectItem value="24">Last 24 months</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <p className="text-xs text-gray-500">Conversion rates between consecutive stages. A case at <code>drawdown</code> implicitly counts as passing every earlier stage.</p>

      {loading && <p className="text-gray-500">Loading…</p>}

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <div className="rounded-lg p-3 bg-slate-100" data-testid="funnel-total-cases">
              <p className="text-xs text-slate-600 uppercase">Total Bookings</p>
              <p className="text-2xl font-bold">{data.total_cases}</p>
            </div>
            <div className="rounded-lg p-3 bg-indigo-100">
              <p className="text-xs text-indigo-700 uppercase">Drawdown</p>
              <p className="text-2xl font-bold text-indigo-800">{data.status_breakdown?.drawdown || 0}</p>
            </div>
            <div className="rounded-lg p-3 bg-red-100">
              <p className="text-xs text-red-700 uppercase">Rejected</p>
              <p className="text-2xl font-bold text-red-800">{data.rejected} <span className="text-sm font-normal">({data.rejection_rate_percent}%)</span></p>
            </div>
            <div className="rounded-lg p-3 bg-green-100">
              <p className="text-xs text-green-700 uppercase">Win Rate</p>
              <p className="text-2xl font-bold text-green-800">
                {data.total_cases ? Math.round(((data.status_breakdown?.drawdown || 0) + (data.status_breakdown?.closed || 0)) / data.total_cases * 100) : 0}%
              </p>
            </div>
          </div>

          <div className="bg-white border rounded-xl p-4 space-y-2" data-testid="funnel-stages">
            {data.stages.map((s, i) => {
              const widthPct = Math.max((s.count / maxCount) * 100, 4);
              const dropoff = i > 0 ? (data.stages[i - 1].count - s.count) : 0;
              return (
                <div key={s.key} className="space-y-1" data-testid={`funnel-stage-${s.key}`}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium text-gray-700">{s.label}</span>
                    <span className="text-xs text-gray-500">
                      <strong>{s.count.toLocaleString()}</strong>
                      {s.conversion_from_prev_percent !== null && (
                        <span className={`ml-2 ${s.conversion_from_prev_percent >= 80 ? 'text-emerald-600' : s.conversion_from_prev_percent >= 40 ? 'text-amber-600' : 'text-red-600'}`}>
                          ({s.conversion_from_prev_percent}% from prev)
                        </span>
                      )}
                      {dropoff > 0 && <span className="ml-2 text-red-500">−{dropoff.toLocaleString()} drop-off</span>}
                    </span>
                  </div>
                  <div className="w-full bg-slate-100 rounded h-6 overflow-hidden">
                    <div
                      className={`h-full ${STAGE_COLOR[i] || 'bg-slate-500'} flex items-center justify-end px-2 text-white text-xs font-medium transition-all`}
                      style={{ width: `${widthPct}%` }}
                    >
                      {s.share_of_total_percent}%
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ============== BANKS ==============
function BanksTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ code: '', name: '', logo_url: '', status: 'active' });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/banks');
      setItems(r.data.items || []);
    } catch (e) { toast.error('Failed to load banks'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm({ code: '', name: '', logo_url: '', status: 'active' }); setShowForm(true); };
  const openEdit = (b) => { setEditing(b); setForm({ code: b.code, name: b.name, logo_url: b.logo_url || '', status: b.status }); setShowForm(true); };
  const save = async () => {
    if (!form.code || !form.name) { toast.error('Code and Name required'); return; }
    try {
      if (editing) await api.put(`/admin/banks/${editing.code}`, { name: form.name, logo_url: form.logo_url || null, status: form.status });
      else await api.post('/admin/banks', { ...form, products: [] });
      toast.success(editing ? 'Bank updated' : 'Bank created');
      setShowForm(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };
  const remove = async (b) => {
    if (!window.confirm(`Delete bank ${b.code}?`)) return;
    try { await api.delete(`/admin/banks/${b.code}`); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div className="space-y-3 mt-4">
      <div className="flex justify-between items-center">
        <h2 className="font-semibold text-gray-800">Participating Banks ({items.length})</h2>
        <Button onClick={openCreate} data-testid="add-bank-btn"><Plus className="w-4 h-4 mr-1" /> Add Bank</Button>
      </div>
      {loading ? <p className="text-sm text-gray-500">Loading…</p> : (
        <div className="border rounded-lg overflow-hidden bg-white">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr><th className="text-left p-2">Code</th><th className="text-left p-2">Name</th><th className="text-left p-2">Products</th><th className="text-left p-2">Status</th><th className="text-right p-2">Actions</th></tr>
            </thead>
            <tbody>
              {items.map(b => (
                <tr key={b.code} className="border-t" data-testid={`bank-row-${b.code}`}>
                  <td className="p-2 font-mono">{b.code}</td>
                  <td className="p-2">{b.name}</td>
                  <td className="p-2 text-xs text-gray-500">{(b.products || []).map(p => p.name).join(', ') || '—'}</td>
                  <td className="p-2"><Badge variant={b.status === 'active' ? 'default' : 'outline'}>{b.status}</Badge></td>
                  <td className="p-2 text-right">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(b)}><Pencil className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" className="text-red-600" onClick={() => remove(b)}><Trash2 className="w-4 h-4" /></Button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={5} className="p-4 text-center text-gray-400">No banks yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? 'Edit Bank' : 'New Bank'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Code *</Label><Input value={form.code} disabled={!!editing} onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="MAYBANK" data-testid="bank-code-input" /></div>
            <div><Label>Name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="bank-name-input" /></div>
            <div><Label>Logo URL</Label><Input value={form.logo_url} onChange={e => setForm({ ...form, logo_url: e.target.value })} /></div>
            <div><Label>Status</Label>
              <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent>
              </Select>
            </div>
            <Button onClick={save} className="w-full" data-testid="save-bank-btn">{editing ? 'Update' : 'Create'}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============== BANKERS ==============
function BankersTab() {
  const [items, setItems] = useState([]);
  const [banks, setBanks] = useState([]);
  const [filterBank, setFilterBank] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ bank_code: '', name: '', team_leader: '', branch: '', phone: '', email: '', status: 'active' });
  const fileRef = useRef(null);

  const load = async () => {
    const q = filterBank ? `?bank_code=${filterBank}` : '';
    try { const r = await api.get(`/admin/bankers${q}`); setItems(r.data.items || []); }
    catch { toast.error('Failed to load bankers'); }
  };
  const loadBanks = async () => {
    try { const r = await api.get('/admin/banks'); setBanks(r.data.items || []); } catch {}
  };
  useEffect(() => { loadBanks(); }, []);
  useEffect(() => { load(); }, [filterBank]);

  const openCreate = () => { setEditing(null); setForm({ bank_code: banks[0]?.code || '', name: '', team_leader: '', branch: '', phone: '', email: '', status: 'active' }); setShowForm(true); };
  const openEdit = (b) => { setEditing(b); setForm({ ...b }); setShowForm(true); };
  const save = async () => {
    if (!form.bank_code || !form.name) { toast.error('Bank and Name required'); return; }
    try {
      const payload = { ...form, agent_specialty: form.agent_specialty || [] };
      if (editing) await api.put(`/admin/bankers/${editing.id}`, payload);
      else await api.post('/admin/bankers', payload);
      toast.success(editing ? 'Banker updated' : 'Banker created');
      setShowForm(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };
  const remove = async (b) => {
    if (!window.confirm(`Delete banker ${b.name}?`)) return;
    try { await api.delete(`/admin/bankers/${b.id}`); toast.success('Deleted'); load(); } catch { toast.error('Failed'); }
  };
  const issueInvite = async (b) => {
    try {
      const r = await api.post(`/admin/bankers/${b.id}/issue-invite`);
      const url = `${window.location.origin}${r.data.setup_url}`;
      await navigator.clipboard.writeText(url).catch(() => {});
      toast.success(`Invite link copied: ${url}`, { duration: 8000 });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to issue invite'); }
  };
  const handleCsvUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try {
      const r = await api.post('/admin/bankers/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Imported ${r.data.inserted} bankers (${r.data.error_count} errors)`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Import failed'); }
    e.target.value = '';
  };

  return (
    <div className="space-y-3 mt-4">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <h2 className="font-semibold text-gray-800">Bankers ({items.length})</h2>
        <div className="flex gap-2 items-center">
          <Select value={filterBank || 'all'} onValueChange={v => setFilterBank(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-40" data-testid="banker-filter-bank"><SelectValue placeholder="All banks" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All banks</SelectItem>{banks.map(b => <SelectItem key={b.code} value={b.code}>{b.code}</SelectItem>)}</SelectContent>
          </Select>
          <input type="file" accept=".csv" ref={fileRef} onChange={handleCsvUpload} className="hidden" />
          <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="banker-csv-import-btn"><Upload className="w-4 h-4 mr-1" /> CSV Import</Button>
          <Button onClick={openCreate} data-testid="add-banker-btn"><Plus className="w-4 h-4 mr-1" /> Add Banker</Button>
        </div>
      </div>
      <div className="border rounded-lg overflow-hidden bg-white">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr><th className="text-left p-2">Bank</th><th className="text-left p-2">Name</th><th className="text-left p-2">Branch</th><th className="text-left p-2">Contact</th><th className="text-left p-2">Status</th><th className="text-right p-2">Actions</th></tr>
          </thead>
          <tbody>
            {items.map(b => (
              <tr key={b.id} className="border-t" data-testid={`banker-row-${b.id}`}>
                <td className="p-2 font-mono">{b.bank_code}</td>
                <td className="p-2">{b.name}{b.team_leader ? <span className="text-xs text-gray-400"> · TL: {b.team_leader}</span> : null}</td>
                <td className="p-2">{b.branch || '—'}</td>
                <td className="p-2 text-xs">{b.phone || ''}{b.phone && b.email ? ' · ' : ''}{b.email || ''}</td>
                <td className="p-2"><Badge variant={b.status === 'active' ? 'default' : 'outline'}>{b.status}</Badge></td>
                <td className="p-2 text-right">
                  <Button size="sm" variant="ghost" onClick={() => issueInvite(b)} title="Issue portal invite" data-testid={`banker-invite-${b.id}`}><Mail className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => openEdit(b)}><Pencil className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" className="text-red-600" onClick={() => remove(b)}><Trash2 className="w-4 h-4" /></Button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="p-4 text-center text-gray-400">No bankers yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-gray-400">CSV columns: <code>bank_code, name, team_leader, branch, phone, email, agent_specialty, status, notes</code> (agent_specialty pipe-separated, e.g. "bumi|non_bumi")</p>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? 'Edit Banker' : 'New Banker'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Bank *</Label>
              <Select value={form.bank_code} onValueChange={v => setForm({ ...form, bank_code: v })}>
                <SelectTrigger data-testid="banker-bank-select"><SelectValue placeholder="Select bank" /></SelectTrigger>
                <SelectContent>{banks.map(b => <SelectItem key={b.code} value={b.code}>{b.code} · {b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="banker-name-input" /></div>
            <div className="grid grid-cols-2 gap-2"><div><Label>Team Leader</Label><Input value={form.team_leader || ''} onChange={e => setForm({ ...form, team_leader: e.target.value })} /></div>
              <div><Label>Branch</Label><Input value={form.branch || ''} onChange={e => setForm({ ...form, branch: e.target.value })} /></div></div>
            <div className="grid grid-cols-2 gap-2"><div><Label>Phone</Label><Input value={form.phone || ''} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
              <div><Label>Email</Label><Input value={form.email || ''} onChange={e => setForm({ ...form, email: e.target.value })} /></div></div>
            <div><Label>Status</Label>
              <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent>
              </Select>
            </div>
            <Button onClick={save} className="w-full" data-testid="save-banker-btn">{editing ? 'Update' : 'Create'}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============== OUTSOURCE PARTNERS ==============
function OutsourceTab() {
  const [items, setItems] = useState([]);
  const [allBankers, setAllBankers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const blank = { name: '', pic_name: '', pic_email: '', pic_phone: '', fee_percent: 0.5, monthly_cap: 30, status: 'active', bankers: [], notes: '' };
  const [form, setForm] = useState(blank);

  const load = async () => {
    try {
      // V4.24.8 — Default to active-only. Toggle allows admins to view
      // inactive partners for reactivation / deletion.
      const [pR, bR] = await Promise.all([
        api.get('/admin/outsource-partners', { params: { status: showInactive ? 'all' : 'active' } }),
        api.get('/admin/bankers'),
      ]);
      setItems(pR.data.items || []);
      setAllBankers((bR.data.items || []).filter(b => b.status === 'active'));
    }
    catch { toast.error('Load failed'); }
  };
  useEffect(() => { load(); }, [showInactive]);  // eslint-disable-line react-hooks/exhaustive-deps

  const openCreate = () => { setEditing(null); setForm(blank); setShowForm(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...blank, ...p }); setShowForm(true); };
  const save = async () => {
    if (!form.name || !form.pic_name) { toast.error('Name and PIC name required'); return; }
    const payload = { ...form, fee_percent: parseFloat(form.fee_percent) || 0, monthly_cap: parseInt(form.monthly_cap) || null };
    try {
      if (editing) await api.put(`/admin/outsource-partners/${editing.id}`, payload);
      else await api.post('/admin/outsource-partners', payload);
      toast.success('Saved'); setShowForm(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Delete ${p.name}?`)) return;
    try { await api.delete(`/admin/outsource-partners/${p.id}`); toast.success('Deleted'); load(); } catch { toast.error('Failed'); }
  };
  const issueInvite = async (p) => {
    if (!p.pic_email) {
      toast.error('Add a PIC email on this partner before issuing an invite.');
      return;
    }
    try {
      const r = await api.post(`/admin/outsource-partners/${p.id}/issue-invite`);
      const url = `${window.location.origin}${r.data.setup_url}`;
      await navigator.clipboard.writeText(url).catch(() => {});
      toast.success(`Invite link copied: ${url}`, { duration: 8000 });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to issue invite'); }
  };

  return (
    <div className="space-y-3 mt-4">
      <div className="flex justify-between items-center">
        <h2 className="font-semibold text-gray-800">Outsource Partners ({items.length})</h2>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer" data-testid="outsource-show-inactive-label">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={e => setShowInactive(e.target.checked)}
              data-testid="outsource-show-inactive"
            />
            Show inactive
          </label>
          <Button onClick={openCreate} data-testid="add-outsource-btn"><Plus className="w-4 h-4 mr-1" /> Add Partner</Button>
        </div>
      </div>
      <div className="border rounded-lg overflow-hidden bg-white">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr><th className="text-left p-2">Partner</th><th className="text-left p-2">PIC</th><th className="text-left p-2">Fee%</th><th className="text-left p-2">Monthly Cap</th><th className="text-left p-2">Bankers</th><th className="text-left p-2">Status</th><th className="text-right p-2">Actions</th></tr>
          </thead>
          <tbody>
            {items.map(p => (
              <tr key={p.id} className="border-t" data-testid={`outsource-row-${p.id}`}>
                <td className="p-2">{p.name}</td>
                <td className="p-2 text-xs">{p.pic_name}{p.pic_email ? <><br /><span className="text-gray-400">{p.pic_email}</span></> : null}</td>
                <td className="p-2">{p.fee_percent}%</td>
                <td className="p-2">{p.monthly_cap || '—'}</td>
                <td className="p-2 text-xs">{(p.bankers || []).length}</td>
                <td className="p-2"><Badge variant={p.status === 'active' ? 'default' : 'outline'}>{p.status}</Badge></td>
                <td className="p-2 text-right">
                  <Button size="sm" variant="ghost" onClick={() => issueInvite(p)} title="Issue portal invite" data-testid={`outsource-invite-${p.id}`}><Mail className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => openEdit(p)}><Pencil className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" className="text-red-600" onClick={() => remove(p)}><Trash2 className="w-4 h-4" /></Button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} className="p-4 text-center text-gray-400">No outsource partners.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? 'Edit Outsource Partner' : 'New Outsource Partner'}</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[70vh] overflow-y-auto">
            <div><Label>Name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="outsource-name-input" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>PIC Name *</Label><Input value={form.pic_name} onChange={e => setForm({ ...form, pic_name: e.target.value })} /></div>
              <div><Label>PIC Phone</Label><Input value={form.pic_phone || ''} onChange={e => setForm({ ...form, pic_phone: e.target.value })} /></div>
            </div>
            <div><Label>PIC Email</Label><Input value={form.pic_email || ''} onChange={e => setForm({ ...form, pic_email: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Fee % (we pay them)</Label><Input type="number" step="0.1" value={form.fee_percent} onChange={e => setForm({ ...form, fee_percent: e.target.value })} /></div>
              <div><Label>Monthly cap (cases)</Label><Input type="number" value={form.monthly_cap || ''} onChange={e => setForm({ ...form, monthly_cap: e.target.value })} /></div>
            </div>
            <div><Label>Status</Label>
              <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Notes</Label><Textarea rows={2} value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>

            {/* V4.24.46 — Banker roster manager (used by outsource portal for multi-fanout)
                V4.24.50 — Roster entries are picked from the panel Bankers list; snapshot fields flow in from participating_bankers. */}
            <div className="border-t pt-3">
              <div className="flex items-center justify-between mb-2">
                <Label>Banker Roster ({(form.bankers || []).length})</Label>
                <Select
                  onValueChange={(pid) => {
                    if (!pid) return;
                    if ((form.bankers || []).some(b => b.panel_banker_id === pid)) {
                      toast.error('That banker is already on the roster.');
                      return;
                    }
                    const pb = (allBankers || []).find(b => b.id === pid);
                    setForm(f => ({
                      ...f,
                      bankers: [...(f.bankers || []), {
                        panel_banker_id: pid,
                        name: pb?.name || '',
                        bank: pb?.bank_code || '',
                        branch: pb?.branch || '',
                        team_leader: pb?.team_leader || '',
                        phone: pb?.phone || '',
                        email: pb?.email || '',
                      }],
                    }));
                  }}
                  value=""
                >
                  <SelectTrigger className="w-64" data-testid="admin-outsource-roster-picker">
                    <SelectValue placeholder="+ Add banker from panel" />
                  </SelectTrigger>
                  <SelectContent>
                    {(allBankers || []).length === 0 && <SelectItem value="__none__" disabled>No active panel bankers</SelectItem>}
                    {(allBankers || []).map(b => (
                      <SelectItem key={b.id} value={b.id}>
                        {(b.bank_code || '—')} · {b.name}{b.branch ? ` (${b.branch})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-gray-500 mb-2">
                Bankers here appear inside the outsource partner&apos;s own portal when they fan cases out in parallel. Select from SAM Rumah&apos;s panel — bank, branch, team leader and contact details snapshot in automatically.
              </p>
              <div className="space-y-2">
                {(form.bankers || []).map((b, idx) => (
                  <div key={b.panel_banker_id || idx} className="border rounded p-2 flex items-center gap-3 bg-slate-50" data-testid={`outsource-roster-row-${idx}`}>
                    <div className="flex-1 text-xs space-y-0.5">
                      <div className="font-medium text-gray-800">{b.bank || '—'} · {b.name || 'Unnamed'}</div>
                      <div className="text-gray-500">
                        {b.branch || '—'} · TL: <span className="text-indigo-700">{b.team_leader || '—'}</span> · {b.phone || 'no phone'} · {b.email || 'no email'}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      type="button"
                      className="text-red-600"
                      onClick={() => setForm(f => ({ ...f, bankers: (f.bankers || []).filter((_, i) => i !== idx) }))}
                      data-testid={`outsource-roster-remove-${idx}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
                {(form.bankers || []).length === 0 && (
                  <p className="text-xs text-gray-400 italic">No bankers yet — pick one from the panel dropdown above.</p>
                )}
              </div>
            </div>

            <Button onClick={save} className="w-full" data-testid="save-outsource-btn">{editing ? 'Update' : 'Create'}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============== LOAN DASHBOARD ==============
function DashboardTab() {
  const [data, setData] = useState({ items: [], meta: { total: 0 }, status_counts: {} });
  const [statusFilter, setStatusFilter] = useState('');
  const [pathFilter, setPathFilter] = useState('');
  const [auditCase, setAuditCase] = useState(null);
  const [auditRows, setAuditRows] = useState([]);
  // Fan-out (Phase B-2)
  const [fanOutCase, setFanOutCase] = useState(null);
  const [allBankers, setAllBankers] = useState([]);
  const [selectedBankers, setSelectedBankers] = useState([]);
  const [fanOutNotes, setFanOutNotes] = useState('');
  const [fanOutBusy, setFanOutBusy] = useState(false);
  // V4.24.14 — Loan Detail dialog (Signed LO upload + Approve/Reject)
  const [detailCase, setDetailCase] = useState(null);
  // V4.24.46 — Assign case to Outsource Partner
  const [outsourceAssignCase, setOutsourceAssignCase] = useState(null);
  const [outsourcePartners, setOutsourcePartners] = useState([]);
  const [selectedOutsourceId, setSelectedOutsourceId] = useState('');
  const [outsourceAssignBusy, setOutsourceAssignBusy] = useState(false);

  // Load all active bankers once (used by fan-out dialog)
  useEffect(() => {
    (async () => {
      try { const r = await api.get('/admin/bankers'); setAllBankers((r.data.items || []).filter(b => b.status === 'active')); }
      catch { /* silent */ }
    })();
  }, []);

  // Load active outsource partners once (used by assign-outsource dialog)
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/admin/outsource-partners', { params: { status: 'active' } });
        setOutsourcePartners(r.data.items || []);
      } catch { /* silent */ }
    })();
  }, []);

  useEffect(() => {
    if (outsourceAssignCase) {
      setSelectedOutsourceId(outsourceAssignCase.outsource_partner_id || '');
    }
  }, [outsourceAssignCase]);

  const submitOutsourceAssign = async () => {
    if (!selectedOutsourceId) { toast.error('Pick an outsource partner'); return; }
    setOutsourceAssignBusy(true);
    try {
      const r = await api.post(`/admin/loans/cases/${outsourceAssignCase.id}/assign-outsource`, { outsource_partner_id: selectedOutsourceId });
      toast.success(r.data.message || 'Assigned');
      setOutsourceAssignCase(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Assign failed'); }
    finally { setOutsourceAssignBusy(false); }
  };

  // When opening fan-out, prefill with case's existing selection
  useEffect(() => {
    if (fanOutCase) {
      setSelectedBankers(fanOutCase.selected_bankers || []);
      setFanOutNotes('');
    }
  }, [fanOutCase]);

  const submitFanOut = async () => {
    if (selectedBankers.length === 0) { toast.error('Select at least one banker'); return; }
    setFanOutBusy(true);
    try {
      await api.patch(`/loans/cases/${fanOutCase.id}/fan-out`, { banker_ids: selectedBankers, notes: fanOutNotes || null });
      toast.success(`Routed to ${selectedBankers.length} banker(s)`);
      setFanOutCase(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fan-out failed'); }
    finally { setFanOutBusy(false); }
  };

  // Assessment wizard (Phase B-3)
  const [assessmentCase, setAssessmentCase] = useState(null);
  const openAssessment = (c) => setAssessmentCase(c);
  const onAssessmentDone = () => { load(); };

  const load = async () => {
    const params = new URLSearchParams();
    if (statusFilter) params.set('status', statusFilter);
    if (pathFilter) params.set('selection_path', pathFilter);
    try {
      // Primary list (richer rows)
      const r = await api.get(`/loans/cases?${params}`);
      // Counts from the admin dashboard summary
      const r2 = await api.get(`/admin/loans/dashboard?${params}`);
      setData({ items: r.data.items, meta: r.data.meta, status_counts: r2.data.status_counts });
    } catch (e) { console.warn(e); toast.error('Load failed'); }
  };
  useEffect(() => { load(); }, [statusFilter, pathFilter]);

  const openAudit = async (c) => {
    setAuditCase(c);
    try { const r = await api.get(`/loans/cases/${c.id}/audit?limit=200`); setAuditRows(r.data.entries || []); }
    catch (e) { toast.error('Failed to load audit'); setAuditRows([]); }
  };

  const statusOptions = ['draft', 'profiling', 'standard', 'special_handling', 'outsource', 'in_review', 'approved', 'drawdown', 'closed', 'rejected'];

  return (
    <div className="space-y-3 mt-4">
      <div className="flex items-center gap-2 flex-wrap">
        <h2 className="font-semibold text-gray-800">Loan Cases ({data.meta.total})</h2>
        <Select value={statusFilter || 'all'} onValueChange={v => setStatusFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-44 ml-auto" data-testid="loan-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {statusOptions.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={pathFilter || 'all'} onValueChange={v => setPathFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-44" data-testid="loan-path-filter"><SelectValue placeholder="All paths" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All paths</SelectItem>
            <SelectItem value="standard">Standard</SelectItem>
            <SelectItem value="special_handling">Special Handling</SelectItem>
            <SelectItem value="outsource">Outsource</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {statusOptions.slice(0, 5).map(s => (
          <div key={s} className={`rounded-lg p-3 ${STATUS_BADGE[s] || 'bg-gray-100'}`} data-testid={`loan-status-card-${s}`}>
            <p className="text-xs uppercase opacity-80">{s.replace('_', ' ')}</p>
            <p className="text-2xl font-bold">{data.status_counts[s] || 0}</p>
          </div>
        ))}
      </div>
      <div className="border rounded-lg overflow-hidden bg-white">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr><th className="text-left p-2">Case Ref</th><th className="text-left p-2">Customer</th><th className="text-left p-2">Property</th><th className="text-right p-2">Loan Amount</th><th className="text-left p-2">Status</th><th className="text-left p-2">Path</th><th className="text-left p-2">Created</th><th className="text-right p-2"></th></tr>
          </thead>
          <tbody>
            {data.items.map(c => (
              <tr key={c.id} className="border-t" data-testid={`loan-case-row-${c.id}`}>
                <td className="p-2 font-mono text-xs">{c.case_ref || c.id?.slice(0, 8)}</td>
                <td className="p-2">{c.customer_name || '—'}<span className="text-xs text-gray-400 block">{c.customer_ic || ''}</span></td>
                <td className="p-2 text-xs">{c.property_name || '—'}</td>
                <td className="p-2 text-right text-xs">{c.loan_amount_requested ? `RM ${Number(c.loan_amount_requested).toLocaleString('en-MY')}` : '—'}</td>
                <td className="p-2"><Badge className={STATUS_BADGE[c.status] || ''}>{c.status}</Badge></td>
                <td className="p-2 text-xs">{c.selection_path || '—'}</td>
                <td className="p-2 text-xs text-gray-500">{c.created_at ? new Date(c.created_at).toLocaleDateString('en-MY') : ''}</td>
                <td className="p-2 text-right whitespace-nowrap">
                  {c.latest_assessment_band && (
                    <Badge
                      className={c.latest_assessment_band === 'green' ? 'bg-emerald-100 text-emerald-700 mr-1' : c.latest_assessment_band === 'amber' ? 'bg-amber-100 text-amber-700 mr-1' : 'bg-red-100 text-red-700 mr-1'}
                      title={`Last assessment: ${c.latest_assessment_band}`}
                      data-testid={`case-band-${c.id}`}
                    >{c.latest_assessment_band}</Badge>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => openAssessment(c)} data-testid={`run-assessment-${c.id}`} title="Run pre-profile assessment"><ShieldCheck className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => setFanOutCase(c)} data-testid={`fan-out-${c.id}`} title="Route to bankers"><Send className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => setOutsourceAssignCase(c)} data-testid={`assign-outsource-${c.id}`} title="Assign to outsource partner"><Briefcase className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => setDetailCase(c)} data-testid={`loan-detail-${c.id}`} title="Loan Detail — Signed LO / Approve / Reject"><FileText className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => openAudit(c)} data-testid={`view-audit-${c.id}`}>Audit</Button>
                </td>
              </tr>
            ))}
            {data.items.length === 0 && <tr><td colSpan={8} className="p-4 text-center text-gray-400">No loan cases match the filter.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!auditCase} onOpenChange={(o) => { if (!o) setAuditCase(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Audit Log — {auditCase?.case_ref}</DialogTitle>
            <DialogDescription className="text-xs text-gray-500">Chronological state changes, in descending order.</DialogDescription>
          </DialogHeader>
          <div className="max-h-96 overflow-y-auto space-y-2">
            {auditRows.length === 0 && <p className="text-sm text-gray-400">No audit entries.</p>}
            {auditRows.map(r => (
              <div key={r.id} className="border-b pb-2 last:border-b-0" data-testid={`audit-entry-${r.id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge className="bg-indigo-100 text-indigo-700 text-[10px]">{r.action}</Badge>
                  {r.before_status && r.after_status && r.before_status !== r.after_status && (
                    <span className="text-xs">
                      <span className="text-red-600 line-through">{r.before_status}</span> → <span className="text-green-700">{r.after_status}</span>
                    </span>
                  )}
                  <span className="text-xs text-gray-500 ml-auto">{r.changed_by_name || r.changed_by} · {new Date(r.changed_at).toLocaleString('en-MY')}</span>
                </div>
                {r.notes && <p className="text-xs text-gray-700 mt-1 italic">"{r.notes}"</p>}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!fanOutCase} onOpenChange={(o) => { if (!o) setFanOutCase(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Route case to bankers — {fanOutCase?.case_ref}</DialogTitle>
            <DialogDescription className="text-xs text-gray-500">
              Select bankers who should review this case. They'll see it in their inbox and can submit offers. You can re-route at any time.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-80 overflow-y-auto border rounded p-2">
            {allBankers.length === 0 && <p className="text-sm text-gray-400">No active bankers. Add bankers in the Bankers tab.</p>}
            {allBankers.map(b => {
              const checked = selectedBankers.includes(b.id);
              return (
                <label key={b.id} className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded cursor-pointer" data-testid={`fan-out-banker-${b.id}`}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={e => {
                      setSelectedBankers(prev => e.target.checked ? [...prev, b.id] : prev.filter(x => x !== b.id));
                    }}
                  />
                  <span className="font-mono text-xs text-gray-500 w-20">{b.bank_code}</span>
                  <span className="flex-1">{b.name}</span>
                  <span className="text-xs text-gray-400">{b.branch || ''}</span>
                </label>
              );
            })}
          </div>
          <div>
            <Label>Notes (optional)</Label>
            <Textarea rows={2} value={fanOutNotes} onChange={e => setFanOutNotes(e.target.value)} data-testid="fan-out-notes" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setFanOutCase(null)}>Cancel</Button>
            <Button onClick={submitFanOut} disabled={fanOutBusy} data-testid="fan-out-submit">
              <Send className="w-4 h-4 mr-1" /> Route to {selectedBankers.length} banker(s)
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AssessmentWizard
        caseId={assessmentCase?.id}
        open={!!assessmentCase}
        onClose={() => setAssessmentCase(null)}
        onCompleted={onAssessmentDone}
      />

      {/* V4.24.14 — Loan Detail Dialog (Signed LO upload + Approve/Reject) */}
      <LoanDetailDialog
        caseRow={detailCase}
        onClose={() => setDetailCase(null)}
        onChanged={() => { load(); }}
      />

      {/* V4.24.46 — Assign to Outsource Partner */}
      <Dialog open={!!outsourceAssignCase} onOpenChange={(o) => { if (!o) setOutsourceAssignCase(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign to Outsource Partner — {outsourceAssignCase?.case_ref}</DialogTitle>
            <DialogDescription className="text-xs text-gray-500">
              The outsource partner will see this case in their own portal (<code>/outsource/dashboard</code>) and can fan it out to bankers on their roster.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Outsource partner</Label>
              <Select value={selectedOutsourceId} onValueChange={setSelectedOutsourceId}>
                <SelectTrigger data-testid="assign-outsource-select"><SelectValue placeholder="Pick a partner" /></SelectTrigger>
                <SelectContent>
                  {outsourcePartners.length === 0 && <SelectItem value="__none__" disabled>No active partners</SelectItem>}
                  {outsourcePartners.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name} · {(p.bankers || []).length} banker{(p.bankers || []).length === 1 ? '' : 's'}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {outsourceAssignCase?.outsource_partner_name && (
                <p className="text-xs text-gray-500 mt-1">Currently assigned to: <span className="font-medium">{outsourceAssignCase.outsource_partner_name}</span></p>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setOutsourceAssignCase(null)}>Cancel</Button>
              <Button onClick={submitOutsourceAssign} disabled={outsourceAssignBusy} data-testid="assign-outsource-submit">
                <Briefcase className="w-4 h-4 mr-1" /> Assign
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>


    </div>
  );
}

// ============== V4.24.14 — Loan Detail Dialog ==============
// Handles: viewing case metadata, uploading the signed Letter of Offer (LO)
// for Loan Type A (Own Bank), and Approve/Reject actions gated on the
// signed LO being present when selection_path === "A_own_bank".
function LoanDetailDialog({ caseRow, onClose, onChanged }) {
  const [signedLo, setSignedLo] = useState({ items: [], latest: null, count: 0 });
  const [bookingStatus, setBookingStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [acting, setActing] = useState(false);
  const fileRef = useRef(null);

  const path = caseRow?.selection_path || '';
  const status = caseRow?.status || '';
  const isOwnBank = path === 'A_own_bank';
  const isSpecialHandling = path === 'D_any_bank';
  // V4.24.35 — Loan Type A AND Loan Type D (Special Handling) both use
  // the same 4-step UI: Select Type → Upload LO → Approve/Reject → SPA Signed.
  const isFourStepFlow = isOwnBank || isSpecialHandling;
  const loanTypeLetter = isSpecialHandling ? 'D' : (isOwnBank ? 'A' : '');
  const loanTypeHint = isSpecialHandling ? 'Type D (Special Handling)' : (isOwnBank ? 'Type A (Own Bank)' : '');
  const isTerminal = ['approved', 'rejected', 'closed', 'drawdown'].includes(status);
  const hasSignedLo = (signedLo.count || 0) > 0;
  const canDecide = isFourStepFlow ? hasSignedLo : true;

  // V4.24.16 / V4.24.35 — 4-step progress for Loan Type A (Own Bank) AND
  // Loan Type D (Special Handling). Both share the exact same UI logic;
  // only Step 1's hint (loan-type label) differs.
  //   Step 1: Select Loan Type       — done when selection_path is A_own_bank or D_any_bank
  //   Step 2: Upload Signed LO       — done when signed_lo has ≥1 upload
  //   Step 3: Approval / Rejected    — done when case.status ∈ {approved, rejected, drawdown, closed}
  //   Step 4: SPA Signed             — done when booking.status ∈ {spa_signed, completed} OR case.status ∈ {drawdown, closed, spa_signed}
  const decisionDone = ['approved', 'rejected', 'drawdown', 'closed'].includes(status);
  const spaDone = ['spa_signed', 'completed'].includes(bookingStatus || '') || ['drawdown', 'closed', 'spa_signed'].includes(status);
  const steps = [
    { key: 'select-type', label: 'Select Loan Type', hint: loanTypeHint, done: isFourStepFlow },
    { key: 'upload-lo', label: 'Upload Signed LO', hint: 'PDF document', done: hasSignedLo },
    { key: 'decision', label: 'Approval / Rejected', hint: status === 'rejected' ? 'Rejected' : (status === 'approved' ? 'Approved' : ''), done: decisionDone },
    { key: 'spa-signed', label: 'SPA Signed', hint: '', done: spaDone },
  ];
  // Current active step = first non-done step (or last, if all done).
  const activeStepIdx = steps.findIndex(s => !s.done);
  const currentIdx = activeStepIdx === -1 ? steps.length - 1 : activeStepIdx;

  useEffect(() => {
    if (!caseRow) return;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/loans/cases/${caseRow.id}/signed-lo`);
        setSignedLo({
          items: r.data.items || [],
          latest: r.data.latest || null,
          count: r.data.count || 0,
        });
      } catch (e) {
        // Empty state — endpoint returns [] for cases with no uploads.
        setSignedLo({ items: [], latest: null, count: 0 });
      } finally {
        setLoading(false);
      }
      // V4.24.16 — Fetch linked booking status so Step 4 (SPA Signed) can
      // reflect completion. Best-effort — silently degrade if not accessible.
      if (caseRow.booking_id) {
        try {
          const rb = await api.get(`/bookings/${caseRow.booking_id}`);
          setBookingStatus(rb.data?.status || rb.data?.booking?.status || null);
        } catch (e) {
          setBookingStatus(null);
        }
      } else {
        setBookingStatus(null);
      }
    })();
  }, [caseRow]);

  const uploadLo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.pdf') && f.type !== 'application/pdf') {
      toast.error('Only PDF files are accepted');
      e.target.value = '';
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', f);
      const r = await api.post(`/loans/cases/${caseRow.id}/signed-lo`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Signed LO uploaded');
      // Refresh list
      setSignedLo(prev => ({
        items: [r.data.signed_lo, ...prev.items],
        latest: r.data.signed_lo,
        count: prev.count + 1,
      }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const setStatus = async (nextStatus, actionLabel) => {
    setActing(true);
    try {
      await api.patch(`/loans/cases/${caseRow.id}/status`, {
        status: nextStatus,
        notes: `${actionLabel} via Loan Detail`,
      });
      toast.success(`Case ${actionLabel}`);
      onChanged?.();
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || `${actionLabel} failed`);
    } finally {
      setActing(false);
    }
  };

  if (!caseRow) return null;

  return (
    <Dialog open={!!caseRow} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="loan-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-indigo-600" />
            Loan Detail — {caseRow.case_ref || caseRow.id?.slice(0, 8)}
          </DialogTitle>
          <DialogDescription>
            {caseRow.customer_name || '—'} · {caseRow.property_name || '—'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* V4.24.16 / V4.24.35 — Loan Type A & D: 4-step stepper (Select Type → Upload LO → Approval → SPA Signed) */}
          {isFourStepFlow && (
            <div
              className="border rounded-lg p-4 bg-gradient-to-br from-indigo-50/60 to-white"
              data-testid={`loan-type-${loanTypeLetter.toLowerCase()}-stepper`}
            >
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">Loan Type {loanTypeLetter} — Progress</p>
              <div className="flex items-start justify-between gap-2">
                {steps.map((s, i) => {
                  const isDone = s.done;
                  const isActive = i === currentIdx && !isDone;
                  const stepNum = i + 1;
                  return (
                    <React.Fragment key={s.key}>
                      <div
                        className="flex-1 flex flex-col items-center text-center"
                        data-testid={`stepper-step-${stepNum}`}
                        data-step-status={isDone ? 'done' : (isActive ? 'active' : 'pending')}
                      >
                        <div
                          className={
                            'w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold transition-all ' +
                            (isDone
                              ? 'bg-emerald-600 text-white'
                              : isActive
                                ? 'bg-indigo-600 text-white ring-4 ring-indigo-200'
                                : 'bg-gray-200 text-gray-500')
                          }
                        >
                          {isDone ? <CheckCircle2 className="w-5 h-5" /> : stepNum}
                        </div>
                        <p
                          className={
                            'text-[11px] mt-1.5 font-medium ' +
                            (isDone ? 'text-emerald-700' : isActive ? 'text-indigo-700' : 'text-gray-500')
                          }
                        >
                          {s.label}
                        </p>
                        {s.hint && (
                          <p className="text-[10px] text-gray-400 mt-0.5">{s.hint}</p>
                        )}
                      </div>
                      {i < steps.length - 1 && (
                        <div className="flex-1 h-0.5 mt-4">
                          <div className={'h-full ' + (steps[i].done ? 'bg-emerald-500' : 'bg-gray-200')} />
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          )}

          {/* Case summary */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm border rounded-lg p-3 bg-slate-50">
            <div><p className="text-[10px] uppercase tracking-wider text-gray-400">Status</p><Badge className="mt-0.5">{status || '—'}</Badge></div>
            <div><p className="text-[10px] uppercase tracking-wider text-gray-400">Selection Path</p><p className="font-medium">{path || '—'}</p></div>
            <div><p className="text-[10px] uppercase tracking-wider text-gray-400">Loan Amount</p><p className="font-medium">{caseRow.loan_amount_requested ? `RM ${Number(caseRow.loan_amount_requested).toLocaleString('en-MY')}` : '—'}</p></div>
          </div>

          {/* Signed LO upload block — relevant for Loan Type A and Type D */}
          {isFourStepFlow ? (
            <div className="border rounded-lg p-4 space-y-3" data-testid="signed-lo-section">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <p className="text-sm font-semibold text-gray-800 flex items-center gap-1">
                    <FileText className="w-4 h-4 text-indigo-600" />
                    Signed Letter of Offer (LO)
                    {hasSignedLo && <Badge className="bg-emerald-100 text-emerald-700 ml-1 text-[10px]">Uploaded</Badge>}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    PDF only. Required before Approve/Reject for Loan Type {loanTypeLetter} ({isSpecialHandling ? 'Special Handling' : 'Own Bank'}).
                  </p>
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  accept="application/pdf"
                  onChange={uploadLo}
                  hidden
                  data-testid="signed-lo-file-input"
                />
                <Button
                  variant={hasSignedLo ? 'outline' : 'default'}
                  size="sm"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading || isTerminal}
                  data-testid="upload-signed-lo-btn"
                >
                  {uploading
                    ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Uploading…</>
                    : <><Upload className="w-4 h-4 mr-1" /> {hasSignedLo ? 'Upload New Version' : 'Upload Signed LO Document'}</>}
                </Button>
              </div>

              {loading && <p className="text-xs text-gray-500">Loading signed LO…</p>}

              {!loading && signedLo.items.length === 0 && (
                <div className="border-dashed border-2 border-gray-200 rounded-lg p-4 text-center bg-gray-50/50">
                  <p className="text-xs text-gray-500">No signed LO uploaded yet.</p>
                </div>
              )}

              {!loading && signedLo.items.length > 0 && (
                <div className="space-y-2">
                  {signedLo.items.map((f, i) => (
                    <a
                      key={i}
                      href={f.url}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-2 border rounded p-2 hover:bg-gray-50 text-xs"
                      data-testid={`signed-lo-item-${i}`}
                    >
                      <FileText className="w-4 h-4 text-indigo-600" />
                      <span className="flex-1 truncate">
                        <span className="font-medium">{f.name}</span>
                        <span className="text-gray-400 ml-1">
                          · {f.size ? `${Math.round(f.size / 1024)} KB` : ''} · {f.uploaded_at ? new Date(f.uploaded_at).toLocaleString('en-MY') : ''}
                          {f.uploaded_by_name ? ` · ${f.uploaded_by_name}` : ''}
                        </span>
                      </span>
                      <Download className="w-3 h-3 text-gray-400" />
                    </a>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="border rounded-lg p-3 text-xs text-gray-500 bg-gray-50">
              Signed LO upload is only required for Loan Type A (Own Bank) or Type D (Special Handling). Current path: <span className="font-medium">{path || '—'}</span>
            </div>
          )}

          {/* Approve / Reject actions */}
          <div className="border rounded-lg p-4">
            <p className="text-sm font-semibold text-gray-800 mb-2">Decision</p>
            {isTerminal ? (
              <p className="text-xs text-gray-500">This case is already <Badge className="ml-1">{status}</Badge> — no further actions available.</p>
            ) : (
              <>
                {isFourStepFlow && !hasSignedLo && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mb-3 flex items-start gap-1">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    Upload the signed LO document first — Approve/Reject are disabled until it&apos;s uploaded.
                  </p>
                )}
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => setStatus('approved', 'approved')}
                    disabled={!canDecide || acting}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="approve-loan-btn"
                  >
                    {acting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                    Approve
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setStatus('rejected', 'rejected')}
                    disabled={!canDecide || acting}
                    className="text-red-600 border-red-200 hover:bg-red-50"
                    data-testid="reject-loan-btn"
                  >
                    {acting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <XCircle className="w-4 h-4 mr-1" />}
                    Reject
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
